import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Package, DollarSign, Clock, CheckCircle, 
  Loader2, RefreshCw, Eye, Trash2, Tag, Plus, Truck, MessageCircle, Lock,
  Pencil, Star, X, TrendingUp, BarChart3, Calendar, ChevronLeft, ChevronRight, Download, FileText
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? "st" : day === 2 || day === 22 ? "nd" : day === 3 || day === 23 ? "rd" : "th";
    return `${day}${suffix} ${d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [newDiscountCode, setNewDiscountCode] = useState("");
  const [newDiscountPercent, setNewDiscountPercent] = useState("10");
  const [newDiscountMaxUses, setNewDiscountMaxUses] = useState("");
  const [creatingDiscount, setCreatingDiscount] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState("");

  // DJ bookings state
  const [djBookings, setDjBookings] = useState([]);
  const [selectedDj, setSelectedDj] = useState(null);
  const [djQuoteAmount, setDjQuoteAmount] = useState("");
  const [djPaymentOption, setDjPaymentOption] = useState("full");

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Manual booking state
  const [showManualBooking, setShowManualBooking] = useState(false);
  const [manualBooking, setManualBooking] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    speaker_id: "", event_date: "", start_time: "", end_time: "",
    event_duration_days: "1", delivery_address: "", delivery_option: "delivery",
    special_requirements: ""
  });

  const downloadContract = (booking, type) => {
    const contractTerms = type === "dj" ? `DJ BOOKING CONTRACT

1. Cancellation by Client: If you cancel the booking, the deposit is non-refundable.
2. Cancellation by The Humble Hires: If we cancel the booking, you will receive a full refund including the deposit.
3. Payment Terms: All payments must be made by the dates stated in your quote. Failure to pay on time may result in cancellation with no refund.
4. Deposit: A deposit is required to secure your booking. The booking is not confirmed until the deposit is received.
5. Remaining Balance: Must be paid as agreed — either 24 hours before the event via bank transfer, or cash on arrival.
6. Event Changes: Any changes must be communicated at least 48 hours in advance.
7. Equipment: Any damage caused to equipment during the event will be charged at full replacement cost.
8. Overtime: Additional time will be charged at a rate to be agreed beforehand.` : `EQUIPMENT HIRE CONTRACT

1. Care of Equipment: The hirer accepts full responsibility for all hired equipment. Any damage, loss or theft will be charged at full replacement cost.
2. Payment: A deposit or full payment is required to secure the booking. Remaining balance must be paid no later than 48 hours before the event.
3. Cancellations: Less than 48 hours = deposit forfeited. More than 48 hours = full refund.
4. Late Returns: Charged at the standard daily rate for each additional day.
5. Setup & Usage: Equipment must be used safely. The Humble Hires is not liable for injuries or damages caused by misuse.
6. Identification: Valid photo ID is required and held on file.
7. Delivery & Collection: Times will be agreed prior to the event. Access must be available.`;

    const content = `
THE HUMBLE HIRES
${type === "dj" ? "DJ Booking" : "Equipment Hire"} Contract
${"=".repeat(50)}

Customer: ${booking.contract_name || booking.customer_name}
Email: ${booking.customer_email}
Phone: ${booking.customer_phone}
${type === "dj" ? `Event Type: ${booking.event_type || "N/A"}` : `Equipment: ${booking.speaker_name}`}
${type === "dj" ? `Venue: ${booking.venue || booking.contract_address || "TBC"}` : `Delivery: ${booking.contract_address || booking.delivery_address}`}
Event Date: ${formatDate(booking.event_date)}
${type === "dj" ? `Hours: ${booking.hours}` : `Duration: ${booking.event_duration_days} day(s)`}
${booking.quote_amount ? `Quote: £${booking.quote_amount.toFixed(2)}` : booking.total_price ? `Total: £${booking.total_price.toFixed(2)}` : ""}

${"=".repeat(50)}
TERMS AND CONDITIONS
${"=".repeat(50)}

${contractTerms}

${"=".repeat(50)}
SIGNED
${"=".repeat(50)}

Signed Name: ${booking.contract_name || booking.customer_name}
Address: ${booking.contract_address || booking.delivery_address || booking.venue || "N/A"}
Date Signed: ${booking.contract_date || "N/A"}
Contract Agreed: Yes

${"=".repeat(50)}
The Humble Hires — thehumblehires@gmail.com
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Contract_${(booking.contract_name || booking.customer_name).replace(/\s+/g, '_')}_${booking.event_date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadId = (imageUrl, customerName) => {
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `ID_${customerName.replace(/\s+/g, '_')}.jpg`;
    a.target = "_blank";
    a.click();
  };

  // Inventory management state
  const [showAddSpeaker, setShowAddSpeaker] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [speakerForm, setSpeakerForm] = useState({
    name: "", model: "", description: "", price_per_day: "",
    image_url: "", quantity: "1", is_bundle: false,
    spec_keys: "", spec_values: ""
  });
  const [savingSpeaker, setSavingSpeaker] = useState(false);
  const [deleteSpeakerDialog, setDeleteSpeakerDialog] = useState(null);

  // Reviews management state
  const [reviews, setReviews] = useState([]);
  const [showAddReview, setShowAddReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: "", rating: "5", comment: "", event_type: ""
  });
  const [savingReview, setSavingReview] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Music state
  const [musicPacks, setMusicPacks] = useState([]);
  const [musicOrders, setMusicOrders] = useState([]);
  const [showAddPack, setShowAddPack] = useState(false);
  const [packForm, setPackForm] = useState({ name: "", description: "", price: "", cover_image: "", download_url: "", tracks: "" });
  const [savingPack, setSavingPack] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) setIsAuthenticated(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("adminToken", response.data.token);
      setIsAuthenticated(true);
      toast.success("Welcome back!");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    setPassword("");
    toast.success("Logged out");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, reviewsRes, packsRes, ordersRes, djRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/music-packs`),
        axios.get(`${API}/music-orders`),
        axios.get(`${API}/dj-bookings`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setReviews(reviewsRes.data);
      setMusicPacks(packsRes.data);
      setMusicOrders(ordersRes.data);
      setDjBookings(djRes.data);
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setAnalytics(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "analytics" && !analytics) fetchAnalytics();
  }, [activeTab]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === "confirmed" && deliveryFee) {
        payload.delivery_fee = parseFloat(deliveryFee);
      }
      const res = await axios.put(`${API}/bookings/${bookingId}/status`, payload);
      toast.success(`Booking status updated to ${newStatus}`);

      // Auto-open WhatsApp confirmation when confirming a booking
      if (newStatus === "confirmed" && res.data) {
        sendWhatsAppConfirmation(res.data);
      }

      // Auto-open WhatsApp deposit paid notification
      if (newStatus === "deposit_paid" && res.data) {
        const b = res.data;
        let phone = b.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
        if (!phone.startsWith('+')) phone = '+' + phone;
        const remaining = (b.total_price / 2).toFixed(2);
        const msg = `Hi ${b.customer_name}! 💰\n\nYour deposit of £${remaining} has been received for your booking!\n\n📦 Equipment: ${b.speaker_name}\n📅 Date: ${formatDate(b.event_date)}\n\n⚠️ Remaining balance: £${remaining} — due 24 hours before your event via bank transfer.\n\nBank Details:\n• Account: Kymani Noble\n• Sort Code: 11-05-03\n• Account No: 17250569\n• Reference: Date of sound hire\n\nThanks! 🔊`;
        window.open(`https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
      }

      // Auto-open WhatsApp payment received when completing a booking
      if (newStatus === "paid" && res.data) {
        sendWhatsAppPaymentReceived(res.data);
      }

      setDeliveryFee("");
      fetchData();
      setSelectedBooking(null);
    } catch {
      toast.error("Failed to update booking status");
    }
  };

  const deleteBooking = async () => {
    if (!bookingToDelete) return;
    try {
      await axios.delete(`${API}/bookings/${bookingToDelete}`);
      toast.success("Booking deleted");
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
      fetchData();
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const createDiscountCode = async () => {
    if (!newDiscountCode.trim()) return;
    setCreatingDiscount(true);
    try {
      const payload = {
        code: newDiscountCode.toUpperCase(),
        discount_percent: parseInt(newDiscountPercent)
      };
      if (newDiscountMaxUses && parseInt(newDiscountMaxUses) > 0) {
        payload.max_uses = parseInt(newDiscountMaxUses);
      }
      await axios.post(`${API}/discount-codes`, payload);
      toast.success(`Discount code ${newDiscountCode.toUpperCase()} created!`);
      setNewDiscountCode("");
      setNewDiscountPercent("10");
      setNewDiscountMaxUses("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create discount code");
    } finally {
      setCreatingDiscount(false);
    }
  };

  const toggleDiscountCode = async (codeId) => {
    try {
      await axios.put(`${API}/discount-codes/${codeId}/toggle`);
      toast.success("Discount code updated");
      fetchData();
    } catch {
      toast.error("Failed to update discount code");
    }
  };

  const deleteDiscountCode = async (codeId) => {
    try {
      await axios.delete(`${API}/discount-codes/${codeId}`);
      toast.success("Discount code deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete discount code");
    }
  };

  // Speaker CRUD
  const resetSpeakerForm = () => {
    setSpeakerForm({
      name: "", model: "", description: "", price_per_day: "",
      image_url: "", quantity: "1", is_bundle: false,
      spec_keys: "", spec_values: ""
    });
  };

  const openAddSpeaker = () => {
    resetSpeakerForm();
    setEditingSpeaker(null);
    setShowAddSpeaker(true);
  };

  const openEditSpeaker = (speaker) => {
    const specKeys = speaker.specs ? Object.keys(speaker.specs).join(", ") : "";
    const specValues = speaker.specs ? Object.values(speaker.specs).join(", ") : "";
    setSpeakerForm({
      name: speaker.name,
      model: speaker.model,
      description: speaker.description,
      price_per_day: speaker.price_per_day.toString(),
      image_url: speaker.image_url || "",
      quantity: speaker.quantity.toString(),
      is_bundle: speaker.is_bundle || false,
      spec_keys: specKeys,
      spec_values: specValues
    });
    setEditingSpeaker(speaker.id);
    setShowAddSpeaker(true);
  };

  const buildSpecs = () => {
    const keys = speakerForm.spec_keys.split(",").map(k => k.trim()).filter(Boolean);
    const values = speakerForm.spec_values.split(",").map(v => v.trim()).filter(Boolean);
    const specs = {};
    keys.forEach((key, i) => {
      if (values[i]) specs[key] = values[i];
    });
    return specs;
  };

  const saveSpeaker = async () => {
    if (!speakerForm.name || !speakerForm.price_per_day) {
      toast.error("Name and price are required");
      return;
    }
    setSavingSpeaker(true);
    try {
      const payload = {
        name: speakerForm.name,
        model: speakerForm.model || speakerForm.name,
        description: speakerForm.description,
        price_per_day: parseFloat(speakerForm.price_per_day),
        image_url: speakerForm.image_url,
        quantity: parseInt(speakerForm.quantity) || 1,
        is_bundle: speakerForm.is_bundle,
        specs: buildSpecs()
      };

      if (editingSpeaker) {
        await axios.put(`${API}/speakers/${editingSpeaker}`, payload);
        toast.success("Equipment updated!");
      } else {
        await axios.post(`${API}/speakers`, payload);
        toast.success("Equipment added!");
      }
      setShowAddSpeaker(false);
      resetSpeakerForm();
      setEditingSpeaker(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save equipment");
    } finally {
      setSavingSpeaker(false);
    }
  };

  const confirmDeleteSpeaker = async () => {
    if (!deleteSpeakerDialog) return;
    try {
      await axios.delete(`${API}/speakers/${deleteSpeakerDialog}`);
      toast.success("Equipment deleted");
      setDeleteSpeakerDialog(null);
      fetchData();
    } catch {
      toast.error("Failed to delete equipment");
    }
  };

  // Reviews CRUD
  const saveReview = async () => {
    if (!reviewForm.customer_name || !reviewForm.comment) {
      toast.error("Name and comment are required");
      return;
    }
    setSavingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        customer_name: reviewForm.customer_name,
        rating: parseInt(reviewForm.rating),
        comment: reviewForm.comment,
        event_type: reviewForm.event_type || null
      });
      toast.success("Review added!");
      setShowAddReview(false);
      setReviewForm({ customer_name: "", rating: "5", comment: "", event_type: "" });
      fetchData();
    } catch {
      toast.error("Failed to add review");
    } finally {
      setSavingReview(false);
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success("Review deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete review");
    }
  };

  // Music Pack CRUD
  const saveMusicPack = async () => {
    if (!packForm.name || !packForm.price) {
      toast.error("Name and price are required");
      return;
    }
    setSavingPack(true);
    try {
      const tracks = packForm.tracks ? packForm.tracks.split("\n").map(t => t.trim()).filter(Boolean) : [];
      await axios.post(`${API}/music-packs`, {
        name: packForm.name,
        description: packForm.description,
        price: parseFloat(packForm.price),
        cover_image: packForm.cover_image,
        download_url: packForm.download_url,
        tracks
      });
      toast.success("Music pack added!");
      setShowAddPack(false);
      setPackForm({ name: "", description: "", price: "", cover_image: "", download_url: "", tracks: "" });
      fetchData();
    } catch {
      toast.error("Failed to add music pack");
    } finally {
      setSavingPack(false);
    }
  };

  const deleteMusicPack = async (packId) => {
    try {
      await axios.delete(`${API}/music-packs/${packId}`);
      toast.success("Music pack deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete pack");
    }
  };

  const updateMusicOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/music-orders/${orderId}/status?status=${status}`);
      toast.success(status === "delivered" ? "Download link sent to customer!" : `Order marked as ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update order");
    }
  };

  const sendWhatsAppConfirmation = (booking) => {
    let phone = booking.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
    if (!phone.startsWith('+')) phone = '+' + phone;
    
    const message = `Hi ${booking.customer_name}! 🎉

Your booking with *The Humble Hires* has been CONFIRMED! ✅

📦 *Equipment:* ${booking.speaker_name}
📅 *Date:* ${formatDate(booking.event_date)}
⏱️ *Duration:* ${booking.event_duration_days} day(s)
📍 *Delivery:* ${booking.delivery_address}
${booking.delivery_fee > 0 ? `🚚 *Setup & Delivery:* £${booking.delivery_fee.toFixed(2)}` : ''}
💰 *Total:* £${booking.total_price.toFixed(2)}

Please complete payment to secure your booking. Bank details:
• Account: Kymani Noble
• Sort Code: 11-05-03
• Account No: 17250569
• Reference: Date of sound hire

Thanks for choosing The Humble Hires! 🔊`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone.replace('+', '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const sendWhatsAppPaymentReceived = (booking) => {
    let phone = booking.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
    if (!phone.startsWith('+')) phone = '+' + phone;
    
    const message = `Hi ${booking.customer_name}! 💚

Your payment has been received! ✅

📦 *Equipment:* ${booking.speaker_name}
📅 *Date:* ${formatDate(booking.event_date)}
💰 *Total Paid:* £${booking.total_price.toFixed(2)}

You're all booked in! We'll be in touch closer to the date with delivery details.

Thanks for choosing The Humble Hires! 🔊`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone.replace('+', '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const filteredBookings = bookings.filter(b => 
    filterStatus === "all" ? true : b.status === filterStatus
  );

  const getStatusClass = (status) => {
    switch (status) {
      case "pending": return "status-pending";
      case "confirmed": return "status-confirmed";
      case "paid": return "status-completed";
      case "deposit_paid": return "bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20";
      case "completed": return "bg-white/10 text-white/70 border border-white/20";
      case "cancelled": return "status-cancelled";
      default: return "";
    }
  };

  if (loading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center px-6">
        <div className="glass-panel p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF0033]/20 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#FF0033]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Admin Access</h1>
            <p className="text-white/60 text-sm">Enter your password to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1E1E1E] border-white/10 text-center text-lg"
              data-testid="admin-password-input"
              autoFocus
            />
            <Button
              type="submit"
              disabled={loginLoading || !password}
              className="w-full bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold"
              data-testid="admin-login-btn"
            >
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter Admin"}
            </Button>
          </form>
          <button
            onClick={() => navigate("/")}
            className="w-full mt-4 text-white/40 hover:text-white text-sm transition-colors"
          >
            Back to website
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="flex items-center text-white/70 hover:text-white transition-colors" data-testid="back-home">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-12 md:h-20" />
            <span className="text-[10px] md:text-xs uppercase tracking-wider text-white/50">Admin</span>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <Button onClick={fetchData} variant="ghost" size="sm" className="text-white/70 hover:text-white px-2 md:px-3" data-testid="refresh-data">
              <RefreshCw className="w-4 h-4" /><span className="hidden md:inline ml-2">Refresh</span>
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#FF0055] hover:text-[#FF0055] hover:bg-[#FF0055]/10 px-2 md:px-3" data-testid="logout-btn">
              <span className="hidden md:inline">Logout</span><X className="w-4 h-4 md:hidden" />
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-20 md:pt-24 pb-16 px-3 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 mb-6 md:mb-8">
            <div className="widget-card" data-testid="stat-total-bookings">
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-5 h-5 text-[#FF0033]" />
                <span className="text-xs uppercase tracking-wider text-white/40">Total Bookings</span>
              </div>
              <p className="widget-value">{stats?.total_bookings || 0}</p>
            </div>
            <div className="widget-card" data-testid="stat-pending">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-xs uppercase tracking-wider text-white/40">Pending</span>
              </div>
              <p className="widget-value text-yellow-500">{stats?.pending_bookings || 0}</p>
            </div>
            <div className="widget-card" data-testid="stat-confirmed">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-[#FF0033]" />
                <span className="text-xs uppercase tracking-wider text-white/40">Confirmed</span>
              </div>
              <p className="widget-value">{stats?.confirmed_bookings || 0}</p>
            </div>
            <div className="widget-card" data-testid="stat-revenue">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[#39FF14]" />
                <span className="text-xs uppercase tracking-wider text-white/40">Revenue</span>
              </div>
              <p className="widget-value text-[#39FF14]">£{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 md:gap-4 mb-6 border-b border-white/10 overflow-x-auto pb-0 -mx-3 px-3 md:mx-0 md:px-0 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {[
              { id: "bookings", label: "Bookings" },
              { id: "dj", label: "DJ" },
              { id: "calendar", label: "Calendar" },
              { id: "inventory", label: "Inventory" },
              { id: "music", label: "Music" },
              { id: "reviews", label: "Reviews" },
              { id: "analytics", label: "Analytics" },
              { id: "contracts", label: "Contracts" },
              { id: "discounts", label: "Discounts" }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-2 md:px-3 text-xs md:text-sm uppercase tracking-wider transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? "text-[#FF0033] border-b-2 border-[#FF0033]"
                    : "text-white/50 hover:text-white"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <>
              <div className="flex items-center justify-between mb-6">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48 bg-[#1A1A1A] border-white/10" data-testid="filter-status">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="all">All Bookings</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setShowManualBooking(!showManualBooking)} className="btn-primary" data-testid="add-manual-booking">
                  <Plus className="w-4 h-4 mr-2" />{showManualBooking ? "Cancel" : "Add Booking"}
                </Button>
              </div>

              {showManualBooking && (
                <div className="glass-panel p-6 mb-6 space-y-4" data-testid="manual-booking-form">
                  <h3 className="text-sm uppercase tracking-wider text-[#FF0033] font-bold">Manual Booking</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Customer Name *</label>
                      <Input value={manualBooking.customer_name} onChange={e => setManualBooking(f => ({ ...f, customer_name: e.target.value }))} placeholder="Full name" className="bg-[#1E1E1E] border-white/10" data-testid="manual-name" />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Email *</label>
                      <Input type="email" value={manualBooking.customer_email} onChange={e => setManualBooking(f => ({ ...f, customer_email: e.target.value }))} placeholder="email@example.com" className="bg-[#1E1E1E] border-white/10" data-testid="manual-email" />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Phone *</label>
                      <Input value={manualBooking.customer_phone} onChange={e => setManualBooking(f => ({ ...f, customer_phone: e.target.value }))} placeholder="07xxx xxxxxx" className="bg-[#1E1E1E] border-white/10" data-testid="manual-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Equipment *</label>
                    <Select value={manualBooking.speaker_id} onValueChange={v => setManualBooking(f => ({ ...f, speaker_id: v }))}>
                      <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="manual-equipment">
                        <SelectValue placeholder="Select equipment or bundle" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {stats?.speakers?.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} — £{s.price_per_day}/day</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Event Date *</label>
                      <Input type="date" value={manualBooking.event_date} onChange={e => setManualBooking(f => ({ ...f, event_date: e.target.value }))} className="bg-[#1E1E1E] border-white/10" data-testid="manual-date" />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Duration (days)</label>
                      <Input type="number" min="0" max="30" value={manualBooking.event_duration_days} onChange={e => setManualBooking(f => ({ ...f, event_duration_days: e.target.value }))} className="bg-[#1E1E1E] border-white/10" data-testid="manual-days" />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Start Time</label>
                      <Input type="time" value={manualBooking.start_time} onChange={e => setManualBooking(f => ({ ...f, start_time: e.target.value }))} className="bg-[#1E1E1E] border-white/10" data-testid="manual-start-time" />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">End Time</label>
                      <Input type="time" value={manualBooking.end_time} onChange={e => setManualBooking(f => ({ ...f, end_time: e.target.value }))} className="bg-[#1E1E1E] border-white/10" data-testid="manual-end-time" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-white/50 mb-1 block">Delivery Option</label>
                      <Select value={manualBooking.delivery_option} onValueChange={v => setManualBooking(f => ({ ...f, delivery_option: v }))}>
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          <SelectItem value="delivery">Delivery & Setup</SelectItem>
                          <SelectItem value="collection">Collection</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {manualBooking.delivery_option === "delivery" && (
                      <div>
                        <label className="text-xs text-white/50 mb-1 block">Delivery Address</label>
                        <Input value={manualBooking.delivery_address} onChange={e => setManualBooking(f => ({ ...f, delivery_address: e.target.value }))} placeholder="Full address" className="bg-[#1E1E1E] border-white/10" data-testid="manual-address" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Notes</label>
                    <Input value={manualBooking.special_requirements} onChange={e => setManualBooking(f => ({ ...f, special_requirements: e.target.value }))} placeholder="Any notes" className="bg-[#1E1E1E] border-white/10" data-testid="manual-notes" />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!manualBooking.customer_name || !manualBooking.customer_email || !manualBooking.customer_phone || !manualBooking.speaker_id || !manualBooking.event_date) {
                        toast.error("Please fill in all required fields");
                        return;
                      }
                      try {
                        const speaker = stats.speakers.find(s => s.id === manualBooking.speaker_id);
                        const days = Math.max(1, parseInt(manualBooking.event_duration_days) || 1);
                        const timeStr = manualBooking.start_time && manualBooking.end_time ? ` (${manualBooking.start_time} - ${manualBooking.end_time})` : "";
                        const notes = [timeStr ? `Time: ${manualBooking.start_time} - ${manualBooking.end_time}` : null, manualBooking.special_requirements].filter(Boolean).join(' | ');
                        await axios.post(`${API}/bookings`, {
                          speaker_id: manualBooking.speaker_id,
                          customer_name: manualBooking.customer_name,
                          customer_email: manualBooking.customer_email,
                          customer_phone: manualBooking.customer_phone,
                          event_date: manualBooking.event_date,
                          event_duration_days: days,
                          delivery_address: manualBooking.delivery_option === "delivery" ? manualBooking.delivery_address : "Collection",
                          quantity: 1,
                          special_requirements: notes || null
                        });
                        toast.success("Booking created!");
                        setShowManualBooking(false);
                        setManualBooking({ customer_name: "", customer_email: "", customer_phone: "", speaker_id: "", event_date: "", start_time: "", end_time: "", event_duration_days: "1", delivery_address: "", delivery_option: "delivery", special_requirements: "" });
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to create booking");
                      }
                    }}
                    className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold w-full"
                    data-testid="submit-manual-booking"
                  >
                    Create Booking
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                {filteredBookings.length === 0 ? (
                  <p className="text-center py-8 text-white/50">No bookings found</p>
                ) : (
                  filteredBookings.map((booking) => (
                    <div key={booking.id} className="glass-panel p-4 hover:border-[#FF0033]/30 transition-colors cursor-pointer" onClick={() => setSelectedBooking(booking)} data-testid={`booking-row-${booking.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-bold truncate">{booking.customer_name}</p>
                            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded flex-shrink-0 ${getStatusClass(booking.status)}`}>
                              {booking.status === "deposit_paid" ? "Deposit Paid" : booking.status}
                            </span>
                          </div>
                          <p className="text-sm text-white/70 truncate">{booking.speaker_name}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/50">
                            <span>{formatDate(booking.event_date)}</span>
                            <span>{booking.event_duration_days} day(s)</span>
                            <span className="font-bold text-[#FF0033]">£{booking.total_price.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedBooking(booking); }} className="text-[#FF0033] hover:bg-[#FF0033]/10 px-2" data-testid={`view-booking-${booking.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setBookingToDelete(booking.id); setDeleteDialogOpen(true); }} className="text-[#FF0055] hover:bg-[#FF0055]/10 px-2" data-testid={`delete-booking-${booking.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* DJ Bookings Tab */}
          {activeTab === "dj" && (
            <div className="space-y-6">
              <p className="text-white/70 text-sm">{djBookings.length} DJ booking request(s)</p>

              {selectedDj ? (
                <div className="glass-panel p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold">DJ Request — {selectedDj.customer_name}</h3>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedDj(null); setDjQuoteAmount(""); setDjPaymentOption("full"); }} className="text-white/50 hover:text-white">
                      <X className="w-4 h-4" /> Close
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-white/50">Name:</span> <span className="font-bold">{selectedDj.customer_name}</span></div>
                    <div><span className="text-white/50">Phone:</span> <span className="font-bold">{selectedDj.customer_phone}</span></div>
                    <div><span className="text-white/50">Email:</span> <span className="font-bold">{selectedDj.customer_email}</span></div>
                    <div><span className="text-white/50">Date:</span> <span className="font-bold">{formatDate(selectedDj.event_date)}</span></div>
                    <div><span className="text-white/50">Hours:</span> <span className="font-bold">{selectedDj.hours}</span></div>
                    <div><span className="text-white/50">Event Type:</span> <span className="font-bold">{selectedDj.event_type || "N/A"}</span></div>
                    <div className="col-span-2"><span className="text-white/50">Venue:</span> <span className="font-bold">{selectedDj.venue || "TBC"}</span></div>
                    {selectedDj.message && <div className="col-span-2"><span className="text-white/50">Message:</span> <span>{selectedDj.message}</span></div>}
                  </div>

                  {selectedDj.status === "pending" && (
                    <div className="border-t border-white/10 pt-4 space-y-4">
                      <h4 className="text-sm uppercase tracking-wider text-[#FF0033] font-bold">Send Quote</h4>
                      <div>
                        <label className="text-xs text-white/50 mb-1 block">Quote Amount (£) *</label>
                        <Input type="number" min="1" value={djQuoteAmount} onChange={e => setDjQuoteAmount(e.target.value)} placeholder="e.g. 200" className="bg-[#1E1E1E] border-white/10 w-40" data-testid="dj-quote-amount" />
                      </div>
                      <div>
                        <label className="text-xs text-white/50 mb-2 block">Payment Option *</label>
                        <div className="flex flex-wrap gap-3">
                          <button type="button" onClick={() => setDjPaymentOption("full")} className={`px-4 py-2 text-sm rounded-lg border transition-all ${djPaymentOption === "full" ? "border-[#FF0033] bg-[#FF0033]/10 text-white" : "border-white/10 text-white/50"}`} data-testid="dj-option-full">
                            Full Payment
                          </button>
                          <button type="button" onClick={() => setDjPaymentOption("deposit")} className={`px-4 py-2 text-sm rounded-lg border transition-all ${djPaymentOption === "deposit" ? "border-[#FF0033] bg-[#FF0033]/10 text-white" : "border-white/10 text-white/50"}`} data-testid="dj-option-deposit">
                            50% Deposit (rest 24hrs before via transfer)
                          </button>
                          <button type="button" onClick={() => setDjPaymentOption("deposit_cash")} className={`px-4 py-2 text-sm rounded-lg border transition-all ${djPaymentOption === "deposit_cash" ? "border-[#FF0033] bg-[#FF0033]/10 text-white" : "border-white/10 text-white/50"}`} data-testid="dj-option-deposit-cash">
                            50% Deposit (rest cash on arrival)
                          </button>
                        </div>
                      </div>
                      {djQuoteAmount && (
                        <div className="bg-[#1E1E1E] border border-white/10 rounded-lg p-4 text-sm space-y-1">
                          <p className="text-white/50">Quote Preview:</p>
                          <p className="font-bold text-[#FF0033] text-lg">Total: £{parseFloat(djQuoteAmount).toFixed(2)}</p>
                          {djPaymentOption === "full" && <p className="text-white/70">Full payment of £{parseFloat(djQuoteAmount).toFixed(2)} required</p>}
                          {djPaymentOption === "deposit" && <p className="text-white/70">Deposit: £{(parseFloat(djQuoteAmount) / 2).toFixed(2)} — Remaining £{(parseFloat(djQuoteAmount) / 2).toFixed(2)} due 24hrs before via transfer</p>}
                          {djPaymentOption === "deposit_cash" && <p className="text-white/70">Deposit: £{(parseFloat(djQuoteAmount) / 2).toFixed(2)} — Remaining £{(parseFloat(djQuoteAmount) / 2).toFixed(2)} cash on arrival</p>}
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button
                          disabled={!djQuoteAmount}
                          onClick={async () => {
                            try {
                              const res = await axios.put(`${API}/dj-bookings/${selectedDj.id}/quote?quote_amount=${djQuoteAmount}&payment_option=${djPaymentOption}`);
                              toast.success("Quote sent to customer!");
                              // Auto-open WhatsApp
                              let phone = selectedDj.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
                              if (!phone.startsWith('+')) phone = '+' + phone;
                              const depositAmt = djPaymentOption === "full" ? parseFloat(djQuoteAmount) : parseFloat(djQuoteAmount) / 2;
                              const remainAmt = parseFloat(djQuoteAmount) - depositAmt;
                              const payMsg = djPaymentOption === "full" ? `Full payment: £${parseFloat(djQuoteAmount).toFixed(2)}` : `Deposit: £${depositAmt.toFixed(2)}\nRemaining: £${remainAmt.toFixed(2)} — ${djPaymentOption === "deposit_cash" ? "cash on arrival" : "due 24hrs before via bank transfer"}`;
                              const msg = `Hi ${selectedDj.customer_name}!\n\nHere's your DJ quote from The Humble Hires:\n\n📅 Date: ${formatDate(selectedDj.event_date)}\n⏱️ Hours: ${selectedDj.hours}\n💰 Total: £${parseFloat(djQuoteAmount).toFixed(2)}\n\n${payMsg}\n\nBank Details:\n• Account: Kymani Noble\n• Sort Code: 11-05-03\n• Account No: 17250569\n• Reference: Date of event\n\nLet me know if you'd like to go ahead! 🔊`;
                              window.open(`https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
                              setSelectedDj(res.data);
                              fetchData();
                            } catch {
                              toast.error("Failed to send quote");
                            }
                          }}
                          className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold"
                          data-testid="send-dj-quote"
                        >
                          Send Quote (Email + WhatsApp)
                        </Button>
                      </div>
                    </div>
                  )}

                  {selectedDj.status !== "pending" && selectedDj.quote_amount && (
                    <div className="border-t border-white/10 pt-4">
                      <div className="bg-[#1E1E1E] border border-white/10 rounded-lg p-4 text-sm space-y-2">
                        <p className="text-[#FF0033] font-bold text-lg">Quoted: £{selectedDj.quote_amount?.toFixed(2)}</p>
                        {selectedDj.payment_option === "full" && <p className="text-white/70">Full payment required</p>}
                        {selectedDj.payment_option === "deposit" && <p className="text-white/70">Deposit: £{selectedDj.deposit_amount?.toFixed(2)} — Remaining: £{selectedDj.remaining_amount?.toFixed(2)} (24hrs before via transfer)</p>}
                        {selectedDj.payment_option === "deposit_cash" && <p className="text-white/70">Deposit: £{selectedDj.deposit_amount?.toFixed(2)} — Remaining: £{selectedDj.remaining_amount?.toFixed(2)} (cash on arrival)</p>}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-white/10 pt-4">
                    <p className="text-white/50 text-xs mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "pending", label: "Pending" },
                        { key: "quoted", label: "Quoted" },
                        { key: "confirmed", label: "Confirmed" },
                        { key: "deposit_paid", label: "Deposit Paid" },
                        { key: "paid", label: "Full Payment Made" },
                        { key: "completed", label: "Completed" },
                        { key: "cancelled", label: "Cancelled" }
                      ].map(s => (
                        <Button
                          key={s.key}
                          variant={selectedDj.status === s.key ? "default" : "outline"}
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/dj-bookings/${selectedDj.id}/status?status=${s.key}`);
                              toast.success(`Status updated to ${s.label}`);
                              fetchData();
                              setSelectedDj(prev => ({ ...prev, status: s.key }));

                              // Auto WhatsApp for deposit paid
                              if (s.key === "deposit_paid" && selectedDj.quote_amount) {
                                let phone = selectedDj.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
                                if (!phone.startsWith('+')) phone = '+' + phone;
                                const deposit = (selectedDj.quote_amount / 2).toFixed(2);
                                const remaining = (selectedDj.quote_amount / 2).toFixed(2);
                                const remainText = selectedDj.payment_option === "deposit_cash" ? "cash on arrival" : "due 24 hours before event via bank transfer";
                                const msg = `Hi ${selectedDj.customer_name}! 💰\n\nYour deposit of £${deposit} for your DJ booking has been received! ✅\n\n📅 Date: ${formatDate(selectedDj.event_date)}\n⏱️ Hours: ${selectedDj.hours}\n\n⚠️ Remaining: £${remaining} — ${remainText}\n\nBank Details:\n• Account: Kymani Noble\n• Sort Code: 11-05-03\n• Account No: 17250569\n• Reference: Date of event\n\nThanks! 🔊`;
                                window.open(`https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
                              }

                              // Auto WhatsApp for full payment
                              if (s.key === "paid") {
                                let phone = selectedDj.customer_phone.replace(/\s+/g, '').replace(/^0/, '44');
                                if (!phone.startsWith('+')) phone = '+' + phone;
                                const msg = `Hi ${selectedDj.customer_name}! 💚\n\nFull payment received for your DJ booking! ✅\n\n📅 Date: ${formatDate(selectedDj.event_date)}\n⏱️ Hours: ${selectedDj.hours}\n💰 Total Paid: £${selectedDj.quote_amount?.toFixed(2)}\n\nYou're all booked in! I'll be in touch closer to the date with setup details.\n\nThanks for choosing The Humble Hires! 🔊`;
                                window.open(`https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
                              }
                            } catch { toast.error("Failed"); }
                          }}
                          className={selectedDj.status === s.key 
                            ? s.key === "deposit_paid" ? "bg-[#FFD700] text-black" 
                            : s.key === "paid" ? "bg-[#39FF14] text-black" 
                            : "bg-[#FF0033] text-black" 
                            : "border-white/20"}
                        >
                          {s.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {djBookings.length === 0 ? (
                    <p className="text-center text-white/50 py-12">No DJ booking requests yet</p>
                  ) : (
                    djBookings.map(dj => (
                      <div key={dj.id} className="glass-panel p-4 flex items-center justify-between cursor-pointer hover:border-[#FF0033]/30 transition-colors" onClick={() => setSelectedDj(dj)} data-testid={`dj-booking-${dj.id}`}>
                        <div>
                          <p className="font-bold">{dj.customer_name}</p>
                          <p className="text-sm text-white/50">{formatDate(dj.event_date)} — {dj.event_type || "DJ Set"} — {dj.hours}hrs</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {dj.quote_amount && <span className="text-[#FF0033] font-bold">£{dj.quote_amount.toFixed(2)}</span>}
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider rounded ${
                            dj.status === "pending" ? "status-pending" :
                            dj.status === "quoted" ? "bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20" :
                            dj.status === "confirmed" ? "status-confirmed" :
                            dj.status === "deposit_paid" ? "bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20" :
                            dj.status === "paid" ? "status-completed" :
                            dj.status === "completed" ? "bg-white/10 text-white/70 border border-white/20" :
                            "status-cancelled"
                          }`}>
                            {dj.status === "deposit_paid" ? "Deposit Paid" : dj.status === "paid" ? "Fully Paid" : dj.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === "calendar" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Hire Calendar</h3>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" className="border-white/20" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} data-testid="cal-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-bold min-w-[140px] text-center">
                    {calendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                  </span>
                  <Button variant="outline" size="sm" className="border-white/20" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} data-testid="cal-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="border-white/20 text-xs" onClick={() => setCalendarMonth(new Date())} data-testid="cal-today">Today</Button>
                </div>
              </div>

              {(() => {
                const year = calendarMonth.getFullYear();
                const month = calendarMonth.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const startDay = firstDay === 0 ? 6 : firstDay - 1;

                // Build events map
                const eventsMap = {};
                const addEvent = (dateStr, event) => {
                  if (!dateStr) return;
                  const d = dateStr.split('T')[0];
                  if (!eventsMap[d]) eventsMap[d] = [];
                  eventsMap[d].push(event);
                };

                bookings.filter(b => b.status !== "cancelled").forEach(b => {
                  addEvent(b.event_date, { type: "booking", name: b.speaker_name, customer: b.customer_name, status: b.status, days: b.event_duration_days || 1 });
                  if (b.event_duration_days > 1) {
                    for (let i = 1; i < b.event_duration_days; i++) {
                      const d = new Date(b.event_date);
                      d.setDate(d.getDate() + i);
                      addEvent(d.toISOString().split('T')[0], { type: "booking", name: b.speaker_name, customer: b.customer_name, status: b.status });
                    }
                  }
                });
                djBookings.filter(b => b.status !== "cancelled").forEach(b => {
                  addEvent(b.event_date, { type: "dj", name: "DJ Set", customer: b.customer_name, status: b.status, hours: b.hours });
                });

                const today = new Date().toISOString().split('T')[0];
                const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

                return (
                  <div className="glass-panel overflow-hidden" data-testid="calendar-grid">
                    <div className="grid grid-cols-7">
                      {days.map(d => (
                        <div key={d} className="text-center text-xs uppercase tracking-wider text-white/40 py-3 border-b border-white/10 font-bold">{d}</div>
                      ))}
                      {Array.from({ length: startDay }).map((_, i) => (
                        <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-white/5 bg-white/[0.01]" />
                      ))}
                      {Array.from({ length: daysInMonth }).map((_, i) => {
                        const day = i + 1;
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const events = eventsMap[dateStr] || [];
                        const isToday = dateStr === today;

                        return (
                          <div key={day} className={`min-h-[100px] border-b border-r border-white/5 p-1.5 transition-colors ${isToday ? 'bg-[#FF0033]/5' : 'hover:bg-white/[0.02]'}`} data-testid={`cal-day-${dateStr}`}>
                            <div className={`text-xs font-bold mb-1 ${isToday ? 'text-[#FF0033]' : 'text-white/50'}`}>
                              {day}
                            </div>
                            <div className="space-y-1">
                              {events.slice(0, 3).map((evt, j) => (
                                <div key={j} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${
                                  evt.type === "dj" 
                                    ? "bg-purple-500/20 text-purple-300 border border-purple-500/20" 
                                    : evt.status === "paid" || evt.status === "completed"
                                      ? "bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20"
                                      : evt.status === "deposit_paid"
                                        ? "bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20"
                                        : "bg-[#FF0033]/10 text-[#FF0033] border border-[#FF0033]/20"
                                }`}>
                                  {evt.type === "dj" ? "🎧 " : "🔊 "}{evt.customer}
                                </div>
                              ))}
                              {events.length > 3 && (
                                <div className="text-[10px] text-white/40 pl-1">+{events.length - 3} more</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-4 border-t border-white/10 flex gap-6 text-xs text-white/50">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#FF0033]/20 border border-[#FF0033]/30" /> Equipment Hire</div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-purple-500/20 border border-purple-500/30" /> DJ Booking</div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#39FF14]/10 border border-[#39FF14]/30" /> Paid/Completed</div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#FFD700]/10 border border-[#FFD700]/30" /> Deposit Paid</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Inventory Tab */}
          {activeTab === "inventory" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <p className="text-white/60 text-sm">{stats?.speakers?.length || 0} items in inventory</p>
                <Button onClick={openAddSpeaker} className="bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold" data-testid="add-equipment-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Equipment
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stats?.speakers?.map((speaker) => (
                  <div key={speaker.id} className={`neon-card p-6 ${speaker.is_bundle ? 'border-[#39FF14]/20' : ''}`} data-testid={`inventory-${speaker.id}`}>
                    <div className="flex items-start gap-4">
                      <img src={speaker.image_url} alt={speaker.name} className="w-20 h-20 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        {speaker.is_bundle && (
                          <span className="text-[10px] uppercase tracking-wider bg-[#39FF14]/20 text-[#39FF14] px-2 py-0.5 mb-1 inline-block">Bundle</span>
                        )}
                        <p className="text-xs uppercase tracking-wider text-[#FF0033] mb-1 mono">{speaker.model}</p>
                        <h3 className="font-bold mb-1 truncate">{speaker.name}</h3>
                        <p className="text-sm text-white/60">£{speaker.price_per_day}/day</p>
                        <p className="text-sm text-white/40">Stock: {speaker.quantity}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider ${speaker.available ? 'status-confirmed' : 'status-cancelled'}`}>
                        {speaker.available ? 'Available' : 'Unavailable'}
                      </span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditSpeaker(speaker)} className="text-[#FF0033] hover:text-[#FF0033] hover:bg-[#FF0033]/10" data-testid={`edit-speaker-${speaker.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteSpeakerDialog(speaker.id)} className="text-[#FF0055] hover:text-[#FF0055] hover:bg-[#FF0055]/10" data-testid={`delete-speaker-${speaker.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Music Tab */}
          {activeTab === "music" && (
            <div className="space-y-6">
              {/* Music Packs */}
              <div className="flex justify-between items-center">
                <h3 className="text-sm uppercase tracking-wider text-white/60">Music Packs ({musicPacks.length})</h3>
                <Button onClick={() => setShowAddPack(true)} className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold" data-testid="add-music-pack-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Pack
                </Button>
              </div>

              {musicPacks.length === 0 ? (
                <div className="glass-panel p-12 text-center">
                  <p className="text-white/40">No music packs yet. Add your first one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {musicPacks.map(pack => (
                    <div key={pack.id} className="neon-card p-4 flex gap-4" data-testid={`music-pack-admin-${pack.id}`}>
                      {pack.cover_image && <img src={pack.cover_image} alt={pack.name} className="w-20 h-20 object-cover rounded" />}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold truncate">{pack.name}</h4>
                        <p className="text-sm text-[#FF0033] font-bold">£{pack.price?.toFixed(2)}</p>
                        <p className="text-xs text-white/40 truncate">{pack.tracks?.length || 0} tracks</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteMusicPack(pack.id)} className="text-[#FF0055] hover:text-[#FF0055]" data-testid={`delete-pack-${pack.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Music Orders */}
              <h3 className="text-sm uppercase tracking-wider text-white/60 mt-8">Orders ({musicOrders.length})</h3>
              <div className="glass-panel overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr><th>Customer</th><th>Pack</th><th>Amount</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {musicOrders.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-8 text-white/40">No orders yet</td></tr>
                      ) : (
                        musicOrders.map(order => (
                          <tr key={order.id} data-testid={`music-order-${order.id}`}>
                            <td>
                              <p className="font-medium">{order.customer_name}</p>
                              <p className="text-xs text-white/40">{order.customer_email}</p>
                            </td>
                            <td>{order.pack_name}</td>
                            <td className="font-bold text-[#FF0033]">£{order.price?.toFixed(2)}</td>
                            <td>
                              <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                                order.status === 'delivered' ? 'status-completed' :
                                order.status === 'paid' ? 'status-confirmed' : 'status-pending'
                              }`}>{order.status}</span>
                            </td>
                            <td>
                              <div className="flex gap-1">
                                {order.status === "pending" && (
                                  <Button variant="ghost" size="sm" onClick={() => updateMusicOrderStatus(order.id, "paid")} className="text-yellow-500 hover:text-yellow-500 text-xs" data-testid={`mark-paid-${order.id}`}>
                                    Mark Paid
                                  </Button>
                                )}
                                {order.status === "paid" && (
                                  <Button variant="ghost" size="sm" onClick={() => updateMusicOrderStatus(order.id, "delivered")} className="text-[#39FF14] hover:text-[#39FF14] text-xs" data-testid={`send-download-${order.id}`}>
                                    Send Download
                                  </Button>
                                )}
                                {order.status === "delivered" && (
                                  <span className="text-xs text-[#39FF14]">Sent</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <p className="text-white/60 text-sm">{reviews.length} reviews</p>
                <Button onClick={() => setShowAddReview(true)} className="bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold" data-testid="add-review-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Review
                </Button>
              </div>

              {reviews.length === 0 ? (
                <div className="glass-panel p-12 text-center">
                  <Star className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40">No reviews yet. Add your first customer review!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="neon-card p-6" data-testid={`review-${review.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold">{review.customer_name}</h4>
                          {review.event_type && (
                            <p className="text-xs text-white/40 mt-1">{review.event_type}</p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteReview(review.id)} className="text-[#FF0055] hover:text-[#FF0055] hover:bg-[#FF0055]/10" data-testid={`delete-review-${review.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`} />
                        ))}
                      </div>
                      <p className="text-white/70 text-sm">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="space-y-6">
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
                </div>
              ) : analytics ? (
                <>
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="glass-panel p-6" data-testid="analytics-revenue">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-[#39FF14]" />
                        <span className="text-xs uppercase tracking-wider text-white/40">Total Revenue</span>
                      </div>
                      <p className="text-2xl font-black text-[#39FF14]">£{analytics.total_revenue.toFixed(2)}</p>
                    </div>
                    <div className="glass-panel p-6" data-testid="analytics-bookings">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-[#FF0033]" />
                        <span className="text-xs uppercase tracking-wider text-white/40">Total Bookings</span>
                      </div>
                      <p className="text-2xl font-black">{analytics.total_bookings}</p>
                    </div>
                    <div className="glass-panel p-6" data-testid="analytics-confirmed">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-[#FF0033]" />
                        <span className="text-xs uppercase tracking-wider text-white/40">Confirmed</span>
                      </div>
                      <p className="text-2xl font-black">{analytics.confirmed_bookings}</p>
                    </div>
                    <div className="glass-panel p-6" data-testid="analytics-avg-value">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-[#FF0033]" />
                        <span className="text-xs uppercase tracking-wider text-white/40">Avg Booking</span>
                      </div>
                      <p className="text-2xl font-black text-[#FF0033]">£{analytics.avg_booking_value.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Revenue Chart */}
                    <div className="glass-panel p-6" data-testid="analytics-monthly-chart">
                      <h3 className="text-sm uppercase tracking-wider text-white/60 mb-6 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[#FF0033]" />Monthly Revenue & Bookings
                      </h3>
                      {analytics.monthly_data.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={analytics.monthly_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="month" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                            <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                            <Tooltip
                              contentStyle={{ background: '#0A0A0A', border: '1px solid #333', borderRadius: '6px' }}
                              labelStyle={{ color: '#fff' }}
                              itemStyle={{ color: '#fff' }}
                              formatter={(value, name) => [name === 'revenue' ? `£${value}` : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
                            />
                            <Bar dataKey="revenue" fill="#FF0033" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="bookings" fill="#39FF14" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-[280px] text-white/30">
                          No booking data yet
                        </div>
                      )}
                    </div>

                    {/* Booking Status Breakdown */}
                    <div className="glass-panel p-6" data-testid="analytics-status-chart">
                      <h3 className="text-sm uppercase tracking-wider text-white/60 mb-6">Booking Status Breakdown</h3>
                      {analytics.status_breakdown.length > 0 ? (
                        <div className="flex items-center gap-8">
                          <ResponsiveContainer width="50%" height={220}>
                            <PieChart>
                              <Pie
                                data={analytics.status_breakdown}
                                dataKey="count"
                                nameKey="status"
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                innerRadius={50}
                              >
                                {analytics.status_breakdown.map((entry, idx) => {
                                  const colors = { pending: '#EAB308', confirmed: '#FF0033', completed: '#39FF14', cancelled: '#FF0055' };
                                  return <Cell key={idx} fill={colors[entry.status] || '#666'} />;
                                })}
                              </Pie>
                              <Tooltip
                                contentStyle={{ background: '#0A0A0A', border: '1px solid #333', borderRadius: '6px' }}
                                labelStyle={{ color: '#fff' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-3">
                            {analytics.status_breakdown.map((item) => {
                              const colors = { pending: '#EAB308', confirmed: '#FF0033', completed: '#39FF14', cancelled: '#FF0055' };
                              return (
                                <div key={item.status} className="flex items-center gap-3">
                                  <div className="w-3 h-3 rounded-full" style={{ background: colors[item.status] || '#666' }} />
                                  <span className="text-sm text-white/70 capitalize">{item.status}</span>
                                  <span className="text-sm font-bold ml-auto">{item.count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[220px] text-white/30">
                          No data yet
                        </div>
                      )}
                    </div>

                    {/* Popular Equipment */}
                    <div className="glass-panel p-6" data-testid="analytics-popular">
                      <h3 className="text-sm uppercase tracking-wider text-white/60 mb-6 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#FF0033]" />Most Popular Equipment
                      </h3>
                      {analytics.popular_equipment.length > 0 ? (
                        <div className="space-y-4">
                          {analytics.popular_equipment.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-bold text-white/30 w-5">{idx + 1}</span>
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-xs text-white/40">{item.hires} hires</span>
                                <span className="text-sm font-bold text-[#39FF14]">£{item.revenue.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-40 text-white/30">
                          No confirmed bookings yet
                        </div>
                      )}
                    </div>

                    {/* Busiest Days */}
                    <div className="glass-panel p-6" data-testid="analytics-busiest-days">
                      <h3 className="text-sm uppercase tracking-wider text-white/60 mb-6 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#FF0033]" />Busiest Days
                      </h3>
                      {analytics.busiest_days.some(d => d.bookings > 0) ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={analytics.busiest_days}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="day" stroke="#666" tick={{ fill: '#666', fontSize: 11 }} tickFormatter={(v) => v.slice(0, 3)} />
                            <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 12 }} allowDecimals={false} />
                            <Tooltip
                              contentStyle={{ background: '#0A0A0A', border: '1px solid #333', borderRadius: '6px' }}
                              labelStyle={{ color: '#fff' }}
                            />
                            <Bar dataKey="bookings" fill="#FF0033" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-[220px] text-white/30">
                          No booking data yet
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-center">
                    <Button onClick={fetchAnalytics} variant="ghost" size="sm" className="text-white/40 hover:text-white" data-testid="refresh-analytics">
                      <RefreshCw className="w-4 h-4 mr-2" />Refresh Analytics
                    </Button>
                  </div>
                </>
              ) : (
                <div className="glass-panel p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40">Failed to load analytics</p>
                  <Button onClick={fetchAnalytics} variant="ghost" size="sm" className="mt-4 text-[#FF0033]">Retry</Button>
                </div>
              )}
            </div>
          )}

          {/* Contracts Tab */}
          {activeTab === "contracts" && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold">Signed Contracts</h3>

              {/* Equipment Hire Contracts */}
              <div>
                <h4 className="text-sm uppercase tracking-wider text-[#FF0033] mb-3 font-bold">Equipment Hire Contracts</h4>
                {bookings.filter(b => b.contract_signed).length === 0 ? (
                  <p className="text-white/50 text-sm py-4">No signed equipment contracts yet</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.filter(b => b.contract_signed).map(b => (
                      <div key={b.id} className="glass-panel p-5" data-testid={`contract-${b.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="font-bold text-lg">{b.customer_name}</p>
                            <p className="text-xs text-white/50">{b.speaker_name}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusClass(b.status)}`}>
                            {b.status === "deposit_paid" ? "Deposit Paid" : b.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-white/50 text-xs mb-1">Signed Name</p>
                            <p className="font-bold">{b.contract_name || b.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Address</p>
                            <p className="font-bold">{b.contract_address || b.delivery_address}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Date Signed</p>
                            <p className="font-bold">{b.contract_date || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Event Date</p>
                            <p className="font-bold">{formatDate(b.event_date)}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Email</p>
                            <p className="font-bold text-xs">{b.customer_email}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Phone</p>
                            <p className="font-bold">{b.customer_phone}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Total</p>
                            <p className="font-bold text-[#FF0033]">£{b.total_price?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Contract Type</p>
                            <p className="font-bold">Equipment Hire</p>
                          </div>
                        </div>
                        {b.id_image && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-white/50 text-xs mb-2">Photo ID</p>
                            <div className="flex items-center gap-3">
                              <img src={b.id_image} alt="ID" className="max-h-32 rounded border border-white/10" />
                              <Button variant="outline" size="sm" onClick={() => downloadId(b.id_image, b.customer_name)} className="border-white/20 hover:border-white/40" data-testid={`download-id-${b.id}`}>
                                <Download className="w-4 h-4 mr-2" />Download ID
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <Button onClick={() => downloadContract(b, "equipment")} className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold" data-testid={`download-contract-${b.id}`}>
                            <FileText className="w-4 h-4 mr-2" />Download Contract
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* DJ Booking Contracts */}
              <div>
                <h4 className="text-sm uppercase tracking-wider text-purple-400 mb-3 font-bold">DJ Booking Contracts</h4>
                {djBookings.filter(b => b.contract_signed).length === 0 ? (
                  <p className="text-white/50 text-sm py-4">No signed DJ contracts yet</p>
                ) : (
                  <div className="space-y-3">
                    {djBookings.filter(b => b.contract_signed).map(b => (
                      <div key={b.id} className="glass-panel p-5 border-purple-500/10" data-testid={`dj-contract-${b.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="font-bold text-lg">{b.customer_name}</p>
                            <p className="text-xs text-purple-400">DJ Booking — {b.event_type || "Event"}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider rounded ${
                            b.status === "quoted" ? "bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20" :
                            b.status === "paid" ? "status-completed" :
                            "status-pending"
                          }`}>{b.status}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-white/50 text-xs mb-1">Signed Name</p>
                            <p className="font-bold">{b.contract_name || b.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Venue / Address</p>
                            <p className="font-bold">{b.contract_address || b.venue || "TBC"}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Date Signed</p>
                            <p className="font-bold">{b.contract_date || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Event Date</p>
                            <p className="font-bold">{formatDate(b.event_date)}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Email</p>
                            <p className="font-bold text-xs">{b.customer_email}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Phone</p>
                            <p className="font-bold">{b.customer_phone}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Hours</p>
                            <p className="font-bold">{b.hours}hrs</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs mb-1">Quote</p>
                            <p className="font-bold text-[#FF0033]">{b.quote_amount ? `£${b.quote_amount.toFixed(2)}` : "Pending"}</p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <Button onClick={() => downloadContract(b, "dj")} className="bg-purple-600 text-white hover:bg-purple-600/80 font-bold" data-testid={`download-dj-contract-${b.id}`}>
                            <FileText className="w-4 h-4 mr-2" />Download Contract
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discounts Tab */}
          {activeTab === "discounts" && (
            <div className="space-y-6">
              <div className="glass-panel p-6">
                <h3 className="text-sm uppercase tracking-wider text-white/60 mb-4">Create New Discount Code</h3>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs text-white/40 mb-1 block">Code</label>
                    <Input value={newDiscountCode} onChange={(e) => setNewDiscountCode(e.target.value.toUpperCase())} placeholder="e.g. SUMMER20" className="bg-black/50 border-white/20 uppercase" data-testid="new-discount-code" />
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-white/40 mb-1 block">% Off</label>
                    <Input type="number" min="1" max="100" value={newDiscountPercent} onChange={(e) => setNewDiscountPercent(e.target.value)} placeholder="10" className="bg-black/50 border-white/20" data-testid="new-discount-percent" />
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-white/40 mb-1 block">Max Uses</label>
                    <Input type="number" min="1" value={newDiscountMaxUses} onChange={(e) => setNewDiscountMaxUses(e.target.value)} placeholder="Unlimited" className="bg-black/50 border-white/20" data-testid="new-discount-max-uses" />
                  </div>
                  <Button onClick={createDiscountCode} disabled={creatingDiscount || !newDiscountCode.trim()} className="btn-primary" data-testid="create-discount">
                    {creatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Create
                  </Button>
                </div>
              </div>

              <div className="glass-panel overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr><th>Code</th><th>Discount</th><th>Uses</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {!stats?.discount_codes?.length ? (
                      <tr><td colSpan="5" className="text-center py-8 text-white/40">No discount codes yet</td></tr>
                    ) : (
                      stats.discount_codes.map((code) => (
                        <tr key={code.id} data-testid={`discount-row-${code.id}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-[#FF0033]" />
                              <span className="font-mono font-bold">{code.code}</span>
                            </div>
                          </td>
                          <td className="text-[#39FF14] font-bold">{code.discount_percent}% OFF</td>
                          <td className="text-white/60 text-sm">
                            {code.times_used || 0}{code.max_uses ? ` / ${code.max_uses}` : ' / Unlimited'}
                          </td>
                          <td>
                            <span className={`px-3 py-1 text-xs uppercase tracking-wider ${code.active ? 'status-confirmed' : 'status-cancelled'}`}>
                              {code.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => toggleDiscountCode(code.id)} className="text-white/60 hover:text-white" data-testid={`toggle-discount-${code.id}`}>
                                {code.active ? 'Disable' : 'Enable'}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteDiscountCode(code.id)} className="text-[#FF0055] hover:text-[#FF0055] hover:bg-[#FF0055]/10" data-testid={`delete-discount-${code.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription className="text-white/40">ID: {selectedBooking?.id}</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><p className="text-white/50 text-xs">Customer</p><p className="font-medium">{selectedBooking.customer_name}</p></div>
                <div><p className="text-white/50 text-xs">Email</p><p className="text-xs break-all">{selectedBooking.customer_email}</p></div>
                <div><p className="text-white/50 text-xs">Phone</p><p>{selectedBooking.customer_phone}</p></div>
                <div><p className="text-white/50 text-xs">Speaker</p><p>{selectedBooking.speaker_name}</p></div>
                <div><p className="text-white/50 text-xs">Event Date</p><p>{formatDate(selectedBooking.event_date)}</p></div>
                <div><p className="text-white/50 text-xs">Duration</p><p>{selectedBooking.event_duration_days} day(s)</p></div>
                <div><p className="text-white/50 text-xs">Quantity</p><p>{selectedBooking.quantity} unit(s)</p></div>
                <div><p className="text-white/50 text-xs">Total</p><p className="text-[#FF0033] font-bold text-lg">£{selectedBooking.total_price.toFixed(2)}</p></div>
              </div>
              <div><p className="text-white/40 text-sm mb-1">Delivery Address</p><p className="text-sm">{selectedBooking.delivery_address}</p></div>
              {selectedBooking.special_requirements && (
                <div><p className="text-white/40 text-sm mb-1">Special Requirements</p><p className="text-sm">{selectedBooking.special_requirements}</p></div>
              )}
              {selectedBooking.id_image && (
                <div>
                  <p className="text-white/40 text-sm mb-2">Customer ID</p>
                  <img src={selectedBooking.id_image} alt="Customer ID" className="w-full max-h-48 object-contain border border-white/10 rounded" />
                </div>
              )}
              {selectedBooking.delivery_fee > 0 && (
                <div className="bg-[#1E1E1E] border border-white/10 p-3 rounded">
                  <div className="flex items-center gap-2 text-[#FF0033]">
                    <Truck className="w-4 h-4" />
                    <span className="text-sm">Setup & Delivery Fee: £{selectedBooking.delivery_fee.toFixed(2)}</span>
                  </div>
                </div>
              )}
              {selectedBooking.status === "pending" && selectedBooking.delivery_address !== "Collection" && (
                <div>
                  <p className="text-white/40 text-sm mb-2">Setup, Delivery & Pickup Fee</p>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#FF0033]" />
                    <span className="text-white/70">£</span>
                    <Input type="number" placeholder="Enter fee amount" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} className="bg-[#1E1E1E] border-white/10 w-32" data-testid="delivery-fee-input" />
                  </div>
                  <p className="text-white/40 text-xs mt-1">This will be added to the total when you confirm</p>
                </div>
              )}
              {selectedBooking.status === "confirmed" && (
                <Button onClick={() => sendWhatsAppConfirmation(selectedBooking)} className="w-full bg-[#25D366] hover:bg-[#25D366]/80 text-white font-bold" data-testid="whatsapp-confirm-btn">
                  <MessageCircle className="w-5 h-5 mr-2" />Send WhatsApp Confirmation
                </Button>
              )}
              <div>
                <p className="text-white/50 text-xs mb-2">Update Status</p>
                <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2">
                  {["pending", "confirmed", "deposit_paid", "paid", "completed", "cancelled"].map((status) => (
                    <Button
                      key={status}
                      variant={selectedBooking.status === status ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateBookingStatus(selectedBooking.id, status)}
                      className={`text-xs ${selectedBooking.status === status
                        ? "bg-[#FF0033] text-black hover:bg-[#FF0033]/80"
                        : "border-white/20 hover:border-white/40"}`}
                      data-testid={`status-${status}`}
                    >
                      {status === "deposit_paid" ? "Deposit" : status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Booking Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10">
          <DialogHeader>
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogDescription className="text-white/40">Are you sure? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-white/20" data-testid="cancel-delete">Cancel</Button>
            <Button onClick={deleteBooking} className="bg-[#FF0055] hover:bg-[#FF0055]/80 text-white" data-testid="confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Speaker Dialog */}
      <Dialog open={showAddSpeaker} onOpenChange={setShowAddSpeaker}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSpeaker ? 'Edit Equipment' : 'Add New Equipment'}</DialogTitle>
            <DialogDescription className="text-white/40">
              {editingSpeaker ? 'Update the details below' : 'Fill in the details for the new item'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Name *</Label>
              <Input value={speakerForm.name} onChange={(e) => setSpeakerForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. RCF ART 715-A" className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Model</Label>
              <Input value={speakerForm.model} onChange={(e) => setSpeakerForm(f => ({ ...f, model: e.target.value }))} placeholder="e.g. RCF 715" className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-model" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Description</Label>
              <Textarea value={speakerForm.description} onChange={(e) => setSpeakerForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the equipment..." rows={3} className="bg-[#1E1E1E] border-white/10 resize-none" data-testid="speaker-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Price (£/day) *</Label>
                <Input type="number" value={speakerForm.price_per_day} onChange={(e) => setSpeakerForm(f => ({ ...f, price_per_day: e.target.value }))} placeholder="50" className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Quantity</Label>
                <Input type="number" value={speakerForm.quantity} onChange={(e) => setSpeakerForm(f => ({ ...f, quantity: e.target.value }))} className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-quantity" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Image URL</Label>
              <Input value={speakerForm.image_url} onChange={(e) => setSpeakerForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-image" />
              {speakerForm.image_url && (
                <img src={speakerForm.image_url} alt="Preview" className="w-20 h-20 object-cover mt-2 rounded border border-white/10" />
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Spec Names (comma separated)</Label>
              <Input value={speakerForm.spec_keys} onChange={(e) => setSpeakerForm(f => ({ ...f, spec_keys: e.target.value }))} placeholder="power, size, weight" className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-spec-keys" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Spec Values (comma separated)</Label>
              <Input value={speakerForm.spec_values} onChange={(e) => setSpeakerForm(f => ({ ...f, spec_values: e.target.value }))} placeholder="1400W, 15 inch, 17.5kg" className="bg-[#1E1E1E] border-white/10" data-testid="speaker-form-spec-values" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="is_bundle" checked={speakerForm.is_bundle} onChange={(e) => setSpeakerForm(f => ({ ...f, is_bundle: e.target.checked }))} className="w-4 h-4" data-testid="speaker-form-bundle" />
              <Label htmlFor="is_bundle" className="text-sm text-white/60 cursor-pointer">This is a Sound Bundle</Label>
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddSpeaker(false)} className="border-white/20">Cancel</Button>
            <Button onClick={saveSpeaker} disabled={savingSpeaker} className="bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold" data-testid="save-speaker-btn">
              {savingSpeaker ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSpeaker ? 'Update' : 'Add Equipment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Speaker Dialog */}
      <Dialog open={!!deleteSpeakerDialog} onOpenChange={() => setDeleteSpeakerDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10">
          <DialogHeader>
            <DialogTitle>Delete Equipment</DialogTitle>
            <DialogDescription className="text-white/40">Are you sure you want to remove this item from your inventory?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeleteSpeakerDialog(null)} className="border-white/20">Cancel</Button>
            <Button onClick={confirmDeleteSpeaker} className="bg-[#FF0055] hover:bg-[#FF0055]/80 text-white" data-testid="confirm-delete-speaker">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Review Dialog */}
      <Dialog open={showAddReview} onOpenChange={setShowAddReview}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Customer Review</DialogTitle>
            <DialogDescription className="text-white/40">Add a testimonial from a past customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Customer Name *</Label>
              <Input value={reviewForm.customer_name} onChange={(e) => setReviewForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="John D." className="bg-[#1E1E1E] border-white/10" data-testid="review-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Event Type</Label>
              <Input value={reviewForm.event_type} onChange={(e) => setReviewForm(f => ({ ...f, event_type: e.target.value }))} placeholder="e.g. Birthday Party, Wedding" className="bg-[#1E1E1E] border-white/10" data-testid="review-form-event" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Rating</Label>
              <div className="flex gap-2 mt-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} type="button" onClick={() => setReviewForm(f => ({ ...f, rating: s.toString() }))} data-testid={`review-star-${s}`}>
                    <Star className={`w-6 h-6 cursor-pointer transition-colors ${s <= parseInt(reviewForm.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-white/20 hover:text-yellow-400/50'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Review *</Label>
              <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))} placeholder="What did the customer say?" rows={4} className="bg-[#1E1E1E] border-white/10 resize-none" data-testid="review-form-comment" />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddReview(false)} className="border-white/20">Cancel</Button>
            <Button onClick={saveReview} disabled={savingReview} className="bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold" data-testid="save-review-btn">
              {savingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Music Pack Dialog */}
      <Dialog open={showAddPack} onOpenChange={setShowAddPack}>
        <DialogContent className="bg-[#1A1A1A] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Music Pack</DialogTitle>
            <DialogDescription className="text-white/40">Add a new song pack for customers to purchase</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Pack Name *</Label>
              <Input value={packForm.name} onChange={e => setPackForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Summer Vibes Vol. 1" className="bg-[#1E1E1E] border-white/10" data-testid="pack-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Description</Label>
              <Textarea value={packForm.description} onChange={e => setPackForm(f => ({ ...f, description: e.target.value }))} placeholder="What's in the pack?" rows={3} className="bg-[#1E1E1E] border-white/10 resize-none" data-testid="pack-form-description" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Price (£) *</Label>
              <Input type="number" value={packForm.price} onChange={e => setPackForm(f => ({ ...f, price: e.target.value }))} placeholder="9.99" className="bg-[#1E1E1E] border-white/10" data-testid="pack-form-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Cover Image URL</Label>
              <Input value={packForm.cover_image} onChange={e => setPackForm(f => ({ ...f, cover_image: e.target.value }))} placeholder="https://..." className="bg-[#1E1E1E] border-white/10" data-testid="pack-form-cover" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Download URL (Google Drive / Dropbox link)</Label>
              <Input value={packForm.download_url} onChange={e => setPackForm(f => ({ ...f, download_url: e.target.value }))} placeholder="https://drive.google.com/..." className="bg-[#1E1E1E] border-white/10" data-testid="pack-form-download" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60 mb-1 block">Track List (one per line)</Label>
              <Textarea value={packForm.tracks} onChange={e => setPackForm(f => ({ ...f, tracks: e.target.value }))} placeholder={"Track 1 - Artist\nTrack 2 - Artist\nTrack 3 - Artist"} rows={5} className="bg-[#1E1E1E] border-white/10 resize-none" data-testid="pack-form-tracks" />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddPack(false)} className="border-white/20">Cancel</Button>
            <Button onClick={saveMusicPack} disabled={savingPack} className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold" data-testid="save-pack-btn">
              {savingPack ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Pack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
